/**
 * Vertragstext als Abschnitte statt HTML: Agenturen bearbeiten Überschriften, Absätze, Listen, Unterschriftsfelder
 * und Bedingungen in einfachen Feldern. Platzhalter ({{brand.name}}, {{var.frist|date}}) bleiben als Text stehen.
 * Fett schreibt man **so**, kursiv *so*, unterstrichen __so__, Zeilenumbrüche mit Enter; Listen haben einen Punkt je Zeile.
 * Überschriften und Absätze lassen sich mittig oder rechtsbündig setzen; „Seitenumbruch“ beginnt im PDF eine neue Seite
 * (PARITAET-R3-8).
 * Alles, was sich nicht sicher abbilden lässt (Tabellen, Links, eigene Klassen), bleibt als HTML-Abschnitt erhalten.
 */
import type { ContractRole } from "@talentkontor/domain";
import { lazyRecord, t } from "../../../lib/sprache";

export type BlockKind = "h1" | "h2" | "h3" | "p" | "note" | "ol" | "ul" | "signature" | "if" | "else" | "endif" | "pagebreak" | "html";
export type BlockAlign = "center" | "right";

export interface Block {
  id: string;
  kind: BlockKind;
  /** Text mit **fett** und Zeilenumbrüchen; bei Listen ein Punkt je Zeile; bei „html“ das rohe HTML */
  text: string;
  /** Unterschriftsfeld: Rolle */
  role?: ContractRole;
  /** Bedingung: Pfad des Platzhalters (z. B. deal.exclusive oder var.exclusive) */
  cond?: string;
  /** Ausrichtung von Überschrift oder Absatz; ohne Angabe linksbündig */
  align?: BlockAlign;
}

/** Arten der Abschnitte im Editor, in der Oberflächensprache (SICHT-AGENTUR-REST-25) */
export const BLOCK_LABEL: Record<BlockKind, string> = lazyRecord<BlockKind>(["h1", "h2", "h3", "p", "note", "ol", "ul", "signature", "if", "else", "endif", "pagebreak", "html"], (k) => ({
  h1: t("Titel", "Title"),
  h2: t("Überschrift", "Heading"),
  h3: t("Zwischenüberschrift", "Subheading"),
  p: t("Absatz", "Paragraph"),
  note: t("Hinweis im Editor (nicht im Vertrag)", "Note in the editor (not in the contract)"),
  ol: t("Nummerierte Liste", "Numbered list"),
  ul: t("Aufzählung", "Bulleted list"),
  signature: t("Unterschriftsfeld", "Signature field"),
  if: t("Wenn …", "If …"),
  else: t("Sonst", "Otherwise"),
  endif: t("Ende der Bedingung", "End of condition"),
  pagebreak: t("Seitenumbruch", "Page break"),
  html: t("HTML (unverändert)", "HTML (unchanged)"),
})[k]);

let counter = 0;
export function blockId(): string {
  counter += 1;
  return `b${Date.now().toString(36)}${counter.toString(36)}`;
}

// ── Inline: HTML ⇄ Text ────────────────────────────────────────────────────────

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', "#39": "'", apos: "'", nbsp: " " };

function decodeEntities(s: string): string {
  return s.replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (_, e: string) => ENTITIES[e] ?? _).replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n)));
}

/** Inline-HTML in Bearbeitungstext; null, wenn Auszeichnungen vorkommen, die der Text nicht tragen kann. */
export function inlineToText(html: string): string | null {
  const collapsed = html.replace(/\s*\n\s*/g, " ");
  const withBreaks = collapsed.replace(/\s*<br\s*\/?>\s*/gi, "\n");
  const withBold = withBreaks.replace(/<(strong|b)>([\s\S]*?)<\/\1>/gi, (_, _t: string, inner: string) => (/[<>]/.test(inner) ? `\u0000` : `**${inner}**`))
    // kursiv und unterstrichen (PARITAET-R3-8), nur ohne weitere Auszeichnung darin
    .replace(/<(em|i)>([\s\S]*?)<\/\1>/gi, (_, _t: string, inner: string) => (/[<>*]/.test(inner) || !inner.trim() ? `\u0000` : `*${inner}*`))
    .replace(/<u>([\s\S]*?)<\/u>/gi, (_, inner: string) => (/[<>_]/.test(inner) || !inner.trim() ? `\u0000` : `__${inner}__`));
  if (withBold.includes("\u0000") || /<[a-z/!]/i.test(withBold)) return null;
  return decodeEntities(withBold).replace(/[ \t]+/g, " ").trim();
}

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Auszeichnungen außerhalb von Platzhaltern: **fett**, __unterstrichen__, *kursiv* (Sternchen nur an Wortgrenzen, „5 €*“ bleibt Text). */
function markup(s: string): string {
  // Platzhalter vorübergehend durch Marken ohne * und _ ersetzen: Auszeichnungen dürfen sie umschließen („**{{agency.name}}**“),
  // aber nie in sie hineinwirken („{{var.frist__tage}}“)
  const held: string[] = [];
  const masked = s.replace(/\{\{[^{}]*\}\}/g, (m) => `\u0001${held.push(m) - 1}\u0002`);
  return masked
    .replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_\s](?:[^_]*?[^_\s])?)__/g, "<u>$1</u>")
    .replace(/(^|[\s(„“"'‚>])\*([^*\s](?:[^*]*?[^*\s])?)\*(?=$|[\s.,;:!?)“"'<])/g, "$1<em>$2</em>")
    .replace(/\u0001(\d+)\u0002/g, (_, i: string) => held[Number(i)] ?? "");
}

/** Bearbeitungstext in Inline-HTML: escapen, **fett**, *kursiv*, __unterstrichen__, Zeilenumbrüche. Platzhalter bleiben unverändert. */
export function textToInline(text: string): string {
  return markup(escapeText(text.trim())).replace(/\r?\n/g, "<br>");
}

// ── Zerlegen ────────────────────────────────────────────────────────────────────

const CONTROL = /^\{\{\s*(#if\s+([^}]+?)|else|\/if|signature:([a-z]+))\s*\}\}/;

/** Passendes schließendes Tag unter Beachtung gleichnamiger Verschachtelung; -1, wenn keins. */
function findClose(html: string, tag: string, from: number): number {
  const re = new RegExp(`<(/?)${tag}\\b[^>]*>`, "gi");
  re.lastIndex = from;
  let depth = 1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    depth += m[1] ? -1 : 1;
    if (depth === 0) return m.index;
  }
  return -1;
}

function attr(attrs: string, name: string): string | null {
  const m = new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, "i").exec(attrs);
  return m ? (m[2] ?? m[3] ?? "") : null;
}

/** Eine Zeile, die ganz in einer Bedingung steht: „{{#if pfad}}Text{{/if}}“ (ohne weitere Bedingung im Text). */
const CONDITIONAL_ITEM = /^\{\{\s*#if\s+([^}]+?)\s*\}\}([\s\S]*)\{\{\s*\/if\s*\}\}$/;
function conditionalItem(line: string): { cond: string; text: string } | null {
  const m = CONDITIONAL_ITEM.exec(line);
  if (!m || /\{\{\s*(#if|else|\/if)\b/.test(m[2]!)) return null;
  return { cond: m[1]!.trim(), text: m[2]! };
}

/**
 * Listenpunkte; ein Punkt, der nur unter einer Bedingung erscheint („{{#if x}}<li>…</li>{{/if}}“), wird zur Zeile
 * „{{#if x}}…{{/if}}“ und beim Zusammensetzen wieder zum bedingten Punkt (keine leere Nummer, wenn x fehlt).
 */
function listItems(inner: string): string[] | null {
  if (/<(ol|ul)\b/i.test(inner)) return null;
  const items: string[] = [];
  const re = /\{\{\s*#if\s+([^}]+?)\s*\}\}\s*<li\b[^>]*>([\s\S]*?)<\/li>\s*\{\{\s*\/if\s*\}\}|<li\b[^>]*>([\s\S]*?)<\/li>/gi;
  let m: RegExpExecArray | null;
  let rest = inner;
  while ((m = re.exec(inner)) !== null) {
    const t = inlineToText(m[3] ?? m[2]!);
    if (t === null || t.includes("\n")) return null;
    if (m[1] !== undefined && /\{\{\s*(#if|else|\/if)\b/.test(t)) return null;
    items.push(m[1] !== undefined ? `{{#if ${m[1].trim()}}}${t}{{/if}}` : t);
    rest = rest.replace(m[0], "");
  }
  return rest.trim() === "" ? items : null;
}

const ROLES = new Set(["agency", "brand", "talent"]);

export function parseBlocks(html: string): Block[] {
  const out: Block[] = [];
  const push = (b: Omit<Block, "id">) => out.push({ id: blockId(), ...b });
  let i = 0;
  const src = html ?? "";
  while (i < src.length) {
    const rest = src.slice(i);
    const ws = /^\s+/.exec(rest);
    if (ws) { i += ws[0].length; continue; }
    const ctl = CONTROL.exec(rest);
    if (ctl) {
      if (ctl[2]) push({ kind: "if", text: "", cond: ctl[2].trim() });
      else if (ctl[1] === "else") push({ kind: "else", text: "" });
      else if (ctl[1] === "/if") push({ kind: "endif", text: "" });
      else if (ctl[3] && ROLES.has(ctl[3])) push({ kind: "signature", text: "", role: ctl[3] as ContractRole });
      else push({ kind: "html", text: ctl[0] });
      i += ctl[0].length;
      continue;
    }
    if (rest.startsWith("<!--")) {
      const end = src.indexOf("-->", i);
      i = end < 0 ? src.length : end + 3;
      continue;
    }
    const open = /^<([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/.exec(rest);
    if (open) {
      const tag = open[1]!.toLowerCase();
      const attrs = open[2] ?? "";
      const innerStart = i + open[0].length;
      const close = /^(br|hr)$/.test(tag) ? innerStart : findClose(src, tag, innerStart);
      if (close < 0) { push({ kind: "html", text: src.slice(i) }); break; }
      const closeTagEnd = /^(br|hr)$/.test(tag) ? innerStart : src.indexOf(">", close) + 1;
      const inner = src.slice(innerStart, close);
      const whole = src.slice(i, closeTagEnd);
      const cls = attr(attrs, "class");
      const alignCls = /^tk-align-(center|right)$/.exec(cls ?? "");
      const align = alignCls ? { align: alignCls[1] as BlockAlign } : {};
      let block: Omit<Block, "id"> | null = null;
      if ((tag === "h1" || tag === "h2" || tag === "h3") && (!cls || alignCls)) {
        const t = inlineToText(inner);
        if (t !== null) block = { kind: tag, text: t, ...align };
      } else if (tag === "p" && (!cls || cls === "tk-sample-notice" || alignCls) && attrs.replace(/\bclass\s*=\s*("[^"]*"|'[^']*')/i, "").trim() === "") {
        const t = inlineToText(inner);
        if (t !== null) block = cls === "tk-sample-notice" ? { kind: "note", text: t } : { kind: "p", text: t, ...align };
      } else if (tag === "div" && cls === "tk-page-break" && inner.trim() === "") {
        block = { kind: "pagebreak", text: "" };
      } else if ((tag === "ol" || tag === "ul") && !cls) {
        const items = listItems(inner);
        if (items) block = { kind: tag, text: items.join("\n") };
      } else if (tag === "div" && cls === "tk-signature") {
        const role = attr(attrs, "data-role");
        if (role && ROLES.has(role) && inner.trim() === "") block = { kind: "signature", text: "", role: role as ContractRole };
      }
      push(block ?? { kind: "html", text: whole });
      i = closeTagEnd;
      continue;
    }
    // Freier Text bis zum nächsten Tag oder Steuerzeichen wird ein Absatz
    const next = rest.search(/<|\{\{\s*(#if|else|\/if|signature:)/);
    const chunk = next < 0 ? rest : rest.slice(0, Math.max(next, 1));
    const t = inlineToText(chunk);
    if (t === null) push({ kind: "html", text: chunk });
    else if (t) push({ kind: "p", text: t });
    i += chunk.length;
  }
  return out;
}

// ── Zusammensetzen ──────────────────────────────────────────────────────────────

const alignAttr = (a: BlockAlign | undefined) => (a === "center" || a === "right" ? ` class="tk-align-${a}"` : "");

export function serializeBlocks(blocks: readonly Block[]): string {
  const parts: string[] = [];
  for (const b of blocks) {
    switch (b.kind) {
      case "h1": case "h2": case "h3":
        if (b.text.trim()) parts.push(`<${b.kind}${alignAttr(b.align)}>${textToInline(b.text)}</${b.kind}>`);
        break;
      case "p":
        if (b.text.trim()) parts.push(`<p${alignAttr(b.align)}>${textToInline(b.text)}</p>`);
        break;
      case "pagebreak":
        parts.push(`<div class="tk-page-break"></div>`);
        break;
      case "note":
        if (b.text.trim()) parts.push(`<p class="tk-sample-notice">${textToInline(b.text)}</p>`);
        break;
      case "ol": case "ul": {
        const items = b.text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        const li = (l: string) => {
          const c = conditionalItem(l);
          return c ? `  {{#if ${c.cond}}}<li>${textToInline(c.text)}</li>{{/if}}` : `  <li>${textToInline(l)}</li>`;
        };
        if (items.length) parts.push(`<${b.kind}>\n${items.map(li).join("\n")}\n</${b.kind}>`);
        break;
      }
      case "signature":
        parts.push(`{{signature:${b.role ?? "talent"}}}`);
        break;
      case "if":
        parts.push(`{{#if ${(b.cond ?? "").trim()}}}`);
        break;
      case "else":
        parts.push("{{else}}");
        break;
      case "endif":
        parts.push("{{/if}}");
        break;
      case "html":
        if (b.text.trim()) parts.push(b.text.trim());
        break;
    }
  }
  return parts.join("\n\n");
}

/** Bedingungen sauber geschachtelt? Liefert Klartext-Befunde für den Editor. */
export function blockProblems(blocks: readonly Block[]): string[] {
  const problems: string[] = [];
  let depth = 0;
  const elseSeen: boolean[] = [];
  blocks.forEach((b, i) => {
    if (b.kind === "if") {
      if (!(b.cond ?? "").trim()) problems.push(t(`Abschnitt ${i + 1}: Bei „Wenn …“ fehlt die Bedingung.`, `Section ${i + 1}: the condition for “If …” is missing.`));
      depth += 1;
      elseSeen.push(false);
    } else if (b.kind === "else") {
      if (depth === 0) problems.push(t(`Abschnitt ${i + 1}: „Sonst“ steht außerhalb einer Bedingung.`, `Section ${i + 1}: “Otherwise” is outside a condition.`));
      else if (elseSeen[elseSeen.length - 1]) problems.push(t(`Abschnitt ${i + 1}: Die Bedingung hat schon ein „Sonst“.`, `Section ${i + 1}: the condition already has an “Otherwise”.`));
      else elseSeen[elseSeen.length - 1] = true;
    } else if (b.kind === "endif") {
      if (depth === 0) problems.push(t(`Abschnitt ${i + 1}: „Ende der Bedingung“ ohne passendes „Wenn …“.`, `Section ${i + 1}: “End of condition” without a matching “If …”.`));
      else { depth -= 1; elseSeen.pop(); }
    }
  });
  if (depth > 0) problems.push(depth === 1 ? t("Eine Bedingung ist nicht geschlossen. Bitte „Ende der Bedingung“ ergänzen.", "A condition is not closed. Please add “End of condition”.")
    : t(`${depth} Bedingungen sind nicht geschlossen. Bitte „Ende der Bedingung“ ergänzen.`, `${depth} conditions are not closed. Please add “End of condition”.`));
  return problems;
}

/** Einrückung je Abschnitt (Tiefe der Bedingungen) für die Anzeige im Editor. */
export function blockDepths(blocks: readonly Block[]): number[] {
  let depth = 0;
  return blocks.map((b) => {
    if (b.kind === "endif") depth = Math.max(0, depth - 1);
    const d = b.kind === "else" ? Math.max(0, depth - 1) : depth;
    if (b.kind === "if") depth += 1;
    return d;
  });
}

// ── Text einfügen (Befund 17) ───────────────────────────────────────────────────

const HEADING = /^(§\s*\d+[a-z]?\b.*|(Art\.|Artikel|Ziffer|Section|Clause)\s+\d+.*|\d{1,2}\.\s+[A-ZÄÖÜ][^.:;]{0,60}|[A-ZÄÖÜ][A-ZÄÖÜ0-9 &\-]{3,60})$/;
const ORDERED = /^\s*(\(?\d{1,2}[.)]|\(?[a-z][.)])\s+/;
const BULLET = /^\s*[-–•*]\s+/;

/**
 * Mehrzeiligen Text (etwa aus Word oder einem PDF) in Abschnitte zerlegen: Leerzeilen trennen Absätze, „§ 3 …“,
 * „Artikel 2 …“ und kurze Zeilen in Großbuchstaben werden Überschriften, „1.“, „(1)“, „a)“ nummerierte Listen,
 * „-“ und „•“ Aufzählungen. Die erste Überschrift ohne § wird zum Titel, wenn der Text damit beginnt.
 */
export function blocksFromText(text: string): Block[] {
  const out: Block[] = [];
  const lines = text.replace(/\r\n?/g, "\n").split("\n").map((l) => l.replace(/\s+$/g, ""));
  let para: string[] = [];
  let list: { kind: "ol" | "ul"; items: string[] } | null = null;
  const flushPara = () => { if (para.length) out.push({ id: blockId(), kind: "p", text: para.join(" ").replace(/\s+/g, " ").trim() }); para = []; };
  const flushList = () => { if (list?.items.length) out.push({ id: blockId(), kind: list.kind, text: list.items.join("\n") }); list = null; };
  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); return; }
    // Erste Zeile, kurz, ohne Satzzeichen am Ende und mit Leerzeile danach: der Titel
    const title = !out.length && !para.length && !list && line.length <= 70 && !/[.;,:]$/.test(line) && !(lines[idx + 1] ?? "").trim() && !ORDERED.test(line) && !BULLET.test(line);
    const heading = title || (HEADING.test(line) && line.length <= 70 && !/[.;,:]$/.test(line) && line.split(/\s+/).length <= 8);
    if (heading) {
      flushPara();
      flushList();
      out.push({ id: blockId(), kind: !out.length && !/^§|^(Art\.|Artikel)/.test(line) ? "h1" : "h2", text: line });
      return;
    }
    if (ORDERED.test(line) || BULLET.test(line)) {
      flushPara();
      const kind = ORDERED.test(line) ? "ol" : "ul";
      if (list && list.kind !== kind) flushList();
      list ??= { kind, items: [] };
      list.items.push(line.replace(ORDERED, "").replace(BULLET, "").trim());
      return;
    }
    if (list) {
      // Folgezeile eines Listenpunkts (Umbruch im Original)
      list.items[list.items.length - 1] += ` ${line}`;
      return;
    }
    para.push(line);
  });
  flushPara();
  flushList();
  return out;
}

/** Stelle für neue Abschnitte: vor dem Unterschriftsbereich am Ende (Überschrift „Unterschriften“ und die Felder), sonst ans Ende. */
export function insertionIndex(blocks: readonly Block[]): number {
  let i = blocks.length;
  while (i > 0 && blocks[i - 1]!.kind === "signature") i -= 1;
  if (i < blocks.length && i > 0 && /^(h2|h3)$/.test(blocks[i - 1]!.kind)) i -= 1;
  return i;
}

/**
 * K-544/K-559 a: Auszeichnung der Markierung umschalten (Fett **, Kursiv *, Unterstrichen __). Fett und Kursiv sind unabhängig:
 * aus **X** wird mit Kursiv ***X***, aus ***X*** ohne Kursiv wieder **X**, ohne Fett *X*. Liefert Text und neue Markierung.
 */
export function auszeichnungUmschalten(tx: string, start: number, end: number, marke: "**" | "*" | "__"): { text: string; a: number; e: number } {
  const markiert = tx.slice(start, end);
  const sterneVor = tx.slice(0, start).match(/\*+$/)?.[0].length ?? 0;
  const sterneNach = tx.slice(end).match(/^\*+/)?.[0].length ?? 0;
  const hat = marke === "*" ? sterneVor % 2 === 1 && sterneNach % 2 === 1
    : marke === "**" ? sterneVor >= 2 && sterneNach >= 2
    : tx.slice(Math.max(0, start - marke.length), start) === marke && tx.slice(end, end + marke.length) === marke;
  if (markiert && hat) return { text: tx.slice(0, start - marke.length) + markiert + tx.slice(end + marke.length), a: start - marke.length, e: end - marke.length };
  return { text: tx.slice(0, start) + marke + markiert + marke + tx.slice(end), a: start + marke.length, e: end + marke.length };
}
